import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  getGetInventoryVehicleDocumentsQueryKey,
  useDeleteInventoryVehicleDocument,
  useGetInventoryVehicleDocuments,
} from '@/api/generated/inventory-vehicles/inventory-vehicles';
import type { VehicleDocumentItem } from '@/api/generated/schemas';
import { Button, ConfirmDialog, EmptyState, Panel, Table, toast } from '@/shared/ui';

import {
  emptyVehicleDocumentFormValues,
  fromVehicleDocumentItem,
  type VehicleDocumentFormValues,
} from '../vehicleDocumentFormSchema';
import { VehicleDocumentDialog } from './VehicleDocumentDialog';

/**
 * Záložka Documents — samostatný agregát s vlastním CRUD a vlastním uložením v dialogu (stejně jako
 * Maintenance). Formulář vozu dokumenty nedrží, takže globální `FormActionBar` se sem nevztahuje.
 */
export function VehicleDocumentsTab({ vehicleId }: { vehicleId?: number }) {
  const { t, i18n } = useTranslation('inventory');
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<VehicleDocumentFormValues | null>(null);
  const [pendingDelete, setPendingDelete] = useState<VehicleDocumentItem | null>(null);

  const documentsQuery = useGetInventoryVehicleDocuments(vehicleId ?? 0, {
    query: { enabled: vehicleId != null },
  });

  const deleteMutation = useDeleteInventoryVehicleDocument({
    mutation: {
      onSuccess: async () => {
        if (vehicleId != null) {
          await queryClient.invalidateQueries({
            queryKey: getGetInventoryVehicleDocumentsQueryKey(vehicleId),
          });
        }
        toast({ severity: 'success', description: t('documents.toast.deleted') });
      },
      onError: () =>
        toast({
          severity: 'error',
          title: t('documents.toast.errorTitle'),
          description: t('documents.toast.deleteError'),
        }),
    },
  });

  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language]
  );

  // Dokument visí na uloženém vozu (FK na dbo.VehicleDoc.FacilityID), takže v create režimu
  // nemá kam patřit — stejně jako u Maintenance.
  if (vehicleId == null) {
    return (
      <EmptyState
        icon="doc"
        title={t('documents.saveVehicleFirstTitle')}
        description={t('documents.saveVehicleFirstDescription')}
      />
    );
  }

  const items = documentsQuery.data?.items ?? [];

  return (
    <>
      <Panel
        variant="filled"
        icon="doc"
        title={t('vehicle.tabs.documents')}
        actions={
          <Button
            type="button"
            variant="ghost"
            size="sm"
            iconLeft="plus"
            onClick={() => setDraft(emptyVehicleDocumentFormValues())}
          >
            {t('documents.add')}
          </Button>
        }
      >
        {documentsQuery.isLoading ? (
          <p className="text-phx-text-muted text-phx-sm">{t('documents.loading')}</p>
        ) : documentsQuery.isError ? (
          <EmptyState icon="alert" title={t('documents.loadError')} />
        ) : items.length === 0 ? (
          <EmptyState icon="doc" title={t('documents.empty')} />
        ) : (
          <div className="border-phx-border rounded-phx overflow-x-auto border">
            <Table density="dense">
              <Table.Head>
                <Table.Row>
                  <Table.HCell density="dense">{t('documents.cols.type')}</Table.HCell>
                  <Table.HCell density="dense">{t('documents.cols.number')}</Table.HCell>
                  <Table.HCell density="dense">{t('documents.cols.issuedOn')}</Table.HCell>
                  <Table.HCell density="dense">{t('documents.cols.validTo')}</Table.HCell>
                  <Table.HCell density="dense">{t('documents.cols.files')}</Table.HCell>
                  <Table.HCell density="dense" className="w-20" />
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {items.map((item) => (
                  <Table.Row key={item.id}>
                    <Table.Cell density="dense">{item.documentTypeName ?? '—'}</Table.Cell>
                    <Table.Cell density="dense" className="font-mono">
                      {item.number}
                    </Table.Cell>
                    <Table.Cell density="dense" className="whitespace-nowrap">
                      {item.issuedOn ? dateFormatter.format(new Date(item.issuedOn)) : '—'}
                    </Table.Cell>
                    <Table.Cell density="dense" className="whitespace-nowrap">
                      {dateFormatter.format(new Date(item.validTo))}
                    </Table.Cell>
                    <Table.Cell density="dense" className="tabular-nums">
                      {item.files.length}
                    </Table.Cell>
                    <Table.Cell density="dense" className="text-right whitespace-nowrap">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        iconLeft="edit"
                        aria-label={t('documents.edit')}
                        onClick={() => setDraft(fromVehicleDocumentItem(item))}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        iconLeft="trash"
                        aria-label={t('documents.remove')}
                        onClick={() => setPendingDelete(item)}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </Panel>

      {draft ? (
        <VehicleDocumentDialog
          vehicleId={vehicleId}
          initialValues={draft}
          onClose={() => setDraft(null)}
        />
      ) : null}

      {pendingDelete ? (
        <ConfirmDialog
          title={t('documents.confirmDelete.title')}
          message={t('documents.confirmDelete.message', { number: pendingDelete.number })}
          onConfirm={() => {
            deleteMutation.mutate({ vehicleId, documentId: pendingDelete.id });
            setPendingDelete(null);
          }}
          onCancel={() => setPendingDelete(null)}
        />
      ) : null}
    </>
  );
}
